import {
  AppDataComponent,
  CompRef,
  DocumentServicesObject,
  Point,
} from '@wix/document-services-types';
import {
  Group,
  Item,
  Section,
} from '@wix/editor-platform-host-integration-apis';
import {
  Preset,
  WidgetSize,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import { PlatformContext } from '../../../types/platformApi';
import { AppData, UnifiedWidget } from '../../../types/unifiedComponents';
import {
  AppDataComponentWithItems,
  EditorClientSpecMapEntryWithSections,
  PlatformAppItemDefinition,
} from './types';
import { getAddUnifiedComponentsMethods } from '../unifiedComponents/installation/addComponents/addUnifiedComponents';
import {
  cleanPresetIdPrefix,
  getSize,
} from '../unifiedComponents/installation/utils';

export const getPresetData = (
  preset: Preset | PlatformAppItemDefinition,
  fallbackSize?: WidgetSize,
): Omit<Item, 'getStructure'> => {
  if ('presetId' in preset) {
    const platformAppItemDefinition = preset as PlatformAppItemDefinition;
    const mobilePresetId =
      platformAppItemDefinition.mobilePresetId &&
      cleanPresetIdPrefix(platformAppItemDefinition.mobilePresetId);
    return {
      id: cleanPresetIdPrefix(platformAppItemDefinition.presetId),
      title: platformAppItemDefinition.desc,
      image: platformAppItemDefinition.image,
      size: {
        width: platformAppItemDefinition.size.width,
        height: platformAppItemDefinition.size.height,
      },
      mobilePresetId,
    };
  } else {
    const unifiedPreset = preset as Preset;
    return {
      id: unifiedPreset.id!,
      title: unifiedPreset.name!,
      image: unifiedPreset.thumbnailUrl!,
      size: getSize(unifiedPreset.breakpoints?.large?.size ?? fallbackSize),
      mobilePresetId: unifiedPreset.breakpoints?.small?.presetId,
    };
  }
};

export const mapDefinitionToItem = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  appData: AppData,
  component: AppDataComponent,
  preset: Preset | PlatformAppItemDefinition,
  presets: Preset[] | PlatformAppItemDefinition[],
): Item => {
  const compData = component.data as UnifiedWidget;
  const fallbackSize = compData.size;
  const presetData = getPresetData(preset, fallbackSize);
  const widgetDefinition = {
    ...compData,
    type: component.type!,
    widgetId: component.componentId!,
  };
  const { getStructure } = getAddUnifiedComponentsMethods(
    { ...context, documentServices },
    appData,
  );

  const getWidgetStructure = (containerRef?: CompRef, position?: Point) =>
    getStructure(
      widgetDefinition,
      {
        defaultSize: presetData.size,
        presets: presets.map(getPresetData, fallbackSize).map((item) => ({
          presetId: item.id,
          name: item.title,
          defaultSize: {
            height: {
              type: 'px',
              value: item.size.height,
            },
            width: {
              type: 'px',
              value: item.size.width,
            },
          },
        })),
      },
      presetData,
      containerRef,
      position,
    );

  return {
    ...presetData,
    getStructure: getWidgetStructure,
  };
};

export const mapDefinitionToSection = (
  { component, items }: AppDataComponentWithItems,
  index: number,
): Section => ({
  id: component.componentId!,
  title: (component.data as UnifiedWidget)?.base?.name ?? component.name!,
  tooltipContent: (component.data as UnifiedWidget)?.base?.description,
  index,
  items,
});

export const mapDefinitionToGroup = (
  { app, sections }: EditorClientSpecMapEntryWithSections,
  index: number,
): Group => ({
  id: app.appDefinitionId,
  title: app.appDefinitionName,
  index,
  sections,
});
